/**
 * WellnessConsentGate — Iter 133.
 *
 * Inline consent panel for wellness services (Fitness / Massage). Shown at
 * the top of the wellness pages. If the patient hasn't yet granted the
 * `wellness_services_participation` consent, we surface a plain-language
 * "opt in" card. Once granted, we collapse to a small "you're opted in"
 * chip with a "withdraw" link.
 *
 * This replaces surfacing the same key inside Account & Privacy →
 * Preferences (see ConsentPrefs.jsx filter for `category === 'wellness'`).
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { HeartHandshake, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function WellnessConsentGate({ service = 'Fitness' }) {
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = () => api.get('/auth/me/consents')
    .then(r => {
      const w = (r.data.consents || []).find(c => c.key === 'wellness_services_participation');
      setRow(w || null);
    })
    .catch(() => setRow(null));

  useEffect(() => { reload(); }, []);

  const toggle = async (grant) => {
    setBusy(true);
    try {
      await api.post('/auth/me/consents', {
        consent_key: 'wellness_services_participation',
        granted: !!grant,
      });
      toast.success(grant ? 'Thanks — you\'re opted in.' : 'Consent withdrawn.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!row) return null;

  // Already opted in — collapsed chip only.
  if (row.granted) {
    return (
      <div
        className="rounded-md border border-emerald-300 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2"
        data-testid="wellness-consent-granted"
      >
        <CheckCircle2 className="w-4 h-4 shrink-0"/>
        <span className="flex-1">
          You&apos;re opted in to {service} services · governed by a separate waiver of liability &amp; non-clinical GDPR basis.
        </span>
        <button
          onClick={() => toggle(false)}
          disabled={busy}
          className="text-[11px] underline hover:text-emerald-950 disabled:opacity-40"
          data-testid="wellness-consent-withdraw"
        >
          Withdraw
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border-2 border-brand bg-brand/5 p-4 mb-4 flex items-start gap-3"
      data-testid="wellness-consent-gate"
    >
      <div className="w-10 h-10 rounded-full bg-brand/10 grid place-items-center shrink-0">
        <HeartHandshake className="w-5 h-5 text-brand"/>
      </div>
      <div className="flex-1 text-sm">
        <div className="font-heading font-semibold mb-1 inline-flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand"/>
          Opt in to {service} services
        </div>
        <p className="text-fg-muted text-xs leading-relaxed mb-3">
          {service} services run under a separate legal basis from clinical care —
          a waiver of liability and non-clinical GDPR processing. To use this
          module please opt in below. You can withdraw at any time (this page
          or Account → Privacy).
        </p>
        <button
          type="button"
          onClick={() => toggle(true)}
          disabled={busy}
          className="btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="wellness-consent-accept"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <CheckCircle2 className="w-3.5 h-3.5"/>}
          I understand — opt me in
        </button>
      </div>
    </div>
  );
}
